// Shared workspace — token in the URL IS the key. The registration doc
// (shares/{token}) points at the canonical property; this view watches the
// canonical doc directly (S4: the workspace mirror is retired, so title/
// status/notes are always live). Signed-in viewers — including anonymous
// guests (owner sign-off #3) — auto-join as members on first load; the rules
// grant self-join while the share is active.
import {
  watchShare, joinShare, updateProperty, removeMember, watchProperty, watchMembers,
} from '../lib/firestore.js';
import { auth, signInWithPopup, GoogleAuthProvider } from '../lib/firebase.js';
import {
  renderNotesThread,
  renderEditorsStrip,
  renderHeaderIdentity,
  STATUSES,
  STATUS_LABELS,
  escapeHtml,
  escapeAttr,
} from './parts.js';

export function renderShared(el, { user, params, navigate }) {
  const token = params[0];
  if (!token) {
    el.innerHTML = '<main><div class="empty">Missing share link.</div></main>';
    return;
  }

  el.innerHTML = `
    <div class="app-header">
      <a class="brand" href="#/map">Property Source</a>
      <nav>${user ? '<a href="#/map">Map</a>' : ''}</nav>
      <div id="header-who">${user ? '' : '<button class="btn" id="sign-in">Sign in to edit</button>'}</div>
    </div>
    <main>
      <div class="card" id="share-card"><div class="muted">Loading…</div></div>
      <div class="card notes-card" id="notes-card" hidden><div id="notes-thread"></div></div>
    </main>`;

  renderHeaderIdentity(el.querySelector('#header-who'), { user, navigate });
  const cardEl = el.querySelector('#share-card');
  const notesCardEl = el.querySelector('#notes-card');
  el.querySelector('#sign-in')?.addEventListener('click', async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (e) {
      if (e.code !== 'auth/popup-closed-by-user' && e.code !== 'auth/cancelled-popup-request') {
        console.warn('sign-in failed', e);
      }
    }
  });

  let share = null;
  let prop = null;
  let members = [];
  let joined = false;
  let unsubProp = null;
  let unsubNotes = null;
  let unsubEditors = null;
  let unsubMembers = null;
  let notesKey = null; // 'pid|canEdit' — re-create the thread only when this changes

  const isOwner = () => user && share && share.owner_uid === user.uid;
  const isMember = () => user && members.some((m) => m.id === user.uid);
  const canEdit = () => !!user && (share?.active === true || isOwner() || isMember());

  function render() {
    if (!share || !share.property_id) {
      notesCardEl.hidden = true;
      cardEl.innerHTML = '<div class="empty">This share link is invalid or was deleted.</div>';
      return;
    }
    if (!user) {
      notesCardEl.hidden = true;
      cardEl.innerHTML = '<div class="empty">Sign in to view this share.<br><span class="muted">You\'ll be connected as an editor.</span></div>';
      return;
    }
    if (share.active === false && !isOwner() && !isMember()) {
      notesCardEl.hidden = true;
      cardEl.innerHTML = '<div class="empty">This share has been revoked.</div>';
      return;
    }
    if (!prop) {
      notesCardEl.hidden = true;
      cardEl.innerHTML = '<div class="empty">You don\'t have access to this property, or it was deleted.</div>';
      return;
    }
    const edit = canEdit();
    const images = prop.metadata?.images?.length
      ? prop.metadata.images
      : prop.metadata?.image_url
        ? [prop.metadata.image_url]
        : [];
    cardEl.innerHTML = `
      ${share.active === false ? '<p class="error">Share revoked — you keep access as an editor.</p>' : ''}
      ${isMember() && !isOwner() ? '<p class="editor-badge">You\'re an editor</p>' : ''}
      <div style="display:flex;gap:14px">
        ${images.length ? `<img class="thumb" style="width:120px;height:90px" src="${escapeAttr(images[0])}" alt="" />` : ''}
        <div style="flex:1">
          ${edit
            ? `<input type="text" id="share-title" class="title-input" value="${escapeAttr(prop.title || '')}" aria-label="Title" />`
            : `<h1>${escapeHtml(prop.title || 'Untitled')}</h1>`}
          <label class="status-select">
            <span>Status</span>
            <select id="status-select" aria-label="Status" ${edit ? '' : 'disabled'}>
              ${STATUSES.map(
                (s) =>
                  `<option value="${s}" ${prop.status === s ? 'selected' : ''}>${STATUS_LABELS[s]}</option>`,
              ).join('')}
            </select>
          </label>
          ${prop.owner_uid !== user.uid ? `<p class="muted creator-line">Created by ${escapeHtml(prop.owner_name || 'Guest')}</p>` : ''}
        </div>
      </div>
      <h2 style="margin-top:16px">Editors</h2>
      <div id="editors"></div>`;

    const title = cardEl.querySelector('#share-title');
    if (title) {
      title.addEventListener('change', () => {
        const t = title.value.trim();
        if (t && t !== prop.title) updateSurface({ title: t });
      });
      title.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') title.blur();
      });
    }
    cardEl.querySelector('#status-select')?.addEventListener('change', (e) => {
      if (edit && e.target.value !== prop.status) updateSurface({ status: e.target.value });
    });

    // The notes thread lives in its own persistent card — re-creating it only
    // when pid or the edit state changes keeps composer focus across the
    // snapshot-driven re-renders of the share card.
    notesCardEl.hidden = false;
    const key = `${share.property_id}|${canEdit()}`;
    if (key !== notesKey) {
      notesKey = key;
      if (unsubNotes) unsubNotes();
      unsubNotes = renderNotesThread(notesCardEl.querySelector('#notes-thread'), {
        pid: share.property_id,
        user,
        disabled: !canEdit(),
      });
    }
    if (unsubEditors) unsubEditors();
    unsubEditors = renderEditorsStrip(cardEl.querySelector('#editors'), {
      token,
      isOwner: isOwner(),
      onRemove: (uid) => removeMember(token, uid),
    });
  }

  async function updateSurface(patch) {
    try {
      await updateProperty(share.property_id, patch);
    } catch (e) {
      console.warn('update failed', e);
    }
  }

  // Join-on-open: first load with a signed-in user (guest OR named) makes
  // membership permanent; the rules allow self-join while the share is
  // active. Join BEFORE watching the canonical property — its read rule needs
  // the member doc to exist (the owner path needs no membership).
  async function ensureJoinAndWatch() {
    if (!share || !share.property_id) return;
    if (user && !joined) {
      joined = true;
      try {
        await joinShare(token, { uid: user.uid, displayName: user.displayName || user.email || 'Guest' });
      } catch (e) {
        console.warn('join failed', e);
        joined = false; // a later share change (e.g. owner re-enables) may succeed
      }
    }
    if (unsubProp) unsubProp();
    unsubProp = watchProperty(share.property_id, (p) => {
      prop = p;
      render();
    });
    if (user && !unsubMembers) {
      unsubMembers = watchMembers(token, (m) => {
        members = m;
        render();
      });
    }
    render();
  }

  const unsubShare = watchShare(token, (s) => {
    share = s;
    ensureJoinAndWatch();
  });

  return () => {
    unsubShare();
    if (unsubProp) unsubProp();
    if (unsubNotes) unsubNotes();
    if (unsubEditors) unsubEditors();
    if (unsubMembers) unsubMembers();
  };
}
